import angular from "angular";
import moment from "moment/moment";

angular
    .module('ui-sentinel.shipments.shipmentTracking')
    .factory('ShipmentTrackingReportsService', ShipmentTrackingReportsService);

ShipmentTrackingReportsService.$inject = ['$rootScope', 'localStorageService', 'ShipmentsService', 'ShipmentsReportService', 'UtilityService', 'FeedbackService'];

function ShipmentTrackingReportsService($rootScope, localStorageService, ShipmentsService, ShipmentsReportService, UtilityService, FeedbackService) {
    const maxPageSize = 500;
    const maxReportCapacity = 500;
    let totalCount = 0;
    let clearedFromDate = undefined;
    let clearedToDate = undefined;

    const service = {
        feedback: FeedbackService,
        shipmentId: null,
        lastShipmentId: null,
        shipmentInfo: null,
        reports: [],
        fullReports: [],
        lastReport: null,
        selected: null,
        isOrs: $rootScope.isOrs,
        init: init,
        load: load,
        get: getReport,
        fromDate: moment().subtract(7, 'day').startOf('day'),
        toDate: moment().add(1, 'day').endOf('day'),
        isFull: false,
        clear: clear
    };

    const keyRoot = 'shipmentTrackingFilter_new_';
    service.dateKey = () => {
        return service.shipmentId ? `${keyRoot}_${service.shipmentId}_` : `${keyRoot}_null_`;
    };

    activate();
    return service;

    function activate() {
        $rootScope.$on('CLIENT_FOCUS_CHANGED', function (event, args) {
            clear();
        });
    }

    function clear() {
        service.selected = null;
        service.reports = [];
        service.lastReport = null;
        service.shipmentId = null;
        service.lastShipmentId = null;
        service.shipmentInfo = null;
        service.isFull = false;

        const savedFromDate = UtilityService.getSetting(`${service.dateKey()}.fromDate`);
        const savedToDate = UtilityService.getSetting(`${service.dateKey()}.toDate`);

        clearedFromDate = moment().subtract(7, 'day').startOf('day');
        clearedToDate = moment().add(1, 'day').endOf('day').startOf('minute');

        service.fromDate = savedFromDate ? moment(savedFromDate) : clearedFromDate;
        service.toDate = savedToDate ? moment(savedToDate) : clearedToDate;
    }

    function init(shipmentId) {
        initAsync(shipmentId).then(() => {
            $rootScope.$apply();
        });
    }

    async function initAsync(shipmentId) {
        clear();

        if (!shipmentId) {
            return;
        }

        service.shipmentId = shipmentId;
        UtilityService.loadingIncrement();

        try {
            const savedFromDate = UtilityService.getSetting(`${service.dateKey()}.fromDate`);
            const savedToDate = UtilityService.getSetting(`${service.dateKey()}.toDate`);

            const [shipment, lastReport] = await Promise.all([
                ShipmentsService.getShipment(shipmentId).$promise,
                ShipmentsReportService.getLatestShipmentReport(shipmentId).$promise
            ]);

            service.shipmentInfo = shipment.shipmentInfo;
            service.lastReport = lastReport;

            if (savedFromDate && savedToDate) {
                service.fromDate = moment(savedFromDate);
                service.toDate = moment(savedToDate);
            } else {
                const from = service.shipmentInfo.trackingStartDate;
                const to = lastReport && service.lastReport.messageTimeStamp ? service.lastReport.messageTimeStamp : service.shipmentInfo.trackingEndDate;

                if (!from && !to) {
                    service.fromDate = moment().subtract(14, 'day');
                    service.toDate = moment();
                } else if (from && !to) {
                    service.fromDate = moment(from);
                    service.toDate = moment(from).add(14, 'day');
                } else if (!from && to) {
                    service.fromDate = moment(to).subtract(14, 'day');
                    service.toDate = moment(to);
                } else {
                    service.fromDate = moment(from);
                    service.toDate = moment(to);

                    const span = service.toDate.diff(service.fromDate, 'days')
                    if (span > 14) {
                        service.fromDate = moment(service.toDate).subtract(14, 'day');
                    }
                }

                if (service.isOrs) {
                    service.fromDate = service.fromDate.utc();
                    service.toDate = service.toDate.utc();
                }

                service.fromDate = service.fromDate.startOf('day');
                service.toDate = service.toDate.endOf('day');
            }

            await load(service.fromDate, service.toDate, true);

        } catch (error) {
            service.feedback.addErrorFromError(error, `Error loading shipment ${shipmentId}`);
            console.log(error);
        } finally {
            UtilityService.loadingDecrement();
        }
    }

    async function load(fromDate, toDate, refresh) {
        if (!service.shipmentId || !service.lastReport || !fromDate || !toDate) {
            clear();
            return;
        }

        const oneYearAgo = moment().subtract(364, 'days');
        const fromMoment = moment.isMoment(fromDate) ? fromDate : moment(fromDate);
        const toMoment = moment.isMoment(toDate) ? toDate : moment(toDate);
        const maxToMoment = moment(fromMoment).add(45, 'day');

        if (fromMoment.isBefore(oneYearAgo)) {
            service.feedback.addNotice(`Invalid date range. From date is over one year old.`);
            return;
        }

        if (toMoment.isBefore(fromMoment)) {
            service.feedback.addNotice(`Invalid date range. To date is before from date.`);
            return;
        }

        if (toMoment.isAfter(maxToMoment)) {
            service.feedback.addNotice(`Invalid date range. Date range exceeds 45 days`);
            return;
        }

        if (!refresh && service.lastShipmentId === service.shipmentId &&
            fromMoment.isSame(service.fromDate) && toMoment.isSame(service.toDate)) {
            //do not reload the same data set again
            return;
        }

        service.lastShipmentId = service.shipmentId;
        service.selected = null;
        service.fromDate = fromMoment;
        service.toDate = toMoment;
        totalCount = 0;

        if (refresh) {
            UtilityService.loadingIncrement();
            try {
                const reports = await ShipmentsReportService.getShipmentReports(service.shipmentId, service.fromDate, service.toDate, 1, maxPageSize).$promise;
                const results = _.orderBy(reports, ['messageTimeStamp'], ['desc']);

                const newList = [];
                for (var i = 0; i < results.length; i++) {
                    const o = results[i];
                    const exist = newList.find(function (element) {
                        return element.reportGuid === o.reportGuid;
                    });
                    if (!exist) {
                        newList.push(o);
                    } else {
                        if (exist.severity === "ok") {
                            var index = newList.find(function (element) {
                                return element.reportGuid === o.reportGuid;
                            });
                            newList[index] = o;
                        }
                    }
                }
                for (var i = newList.length - 1; i >= 0; i--) {
                    var r = newList[i];
                    const activatedAlarms = r.activatedAlarms.map(function (item) {
                        return item.alarmName;
                    });
                    r.activatedAlarms = r.activatedAlarms.filter(function (item, index) {
                        return activatedAlarms.indexOf(item.alarmName) >= index;
                    });
                    r.isShockExceeded = r.shockMagnitude != null || (r.shockX != null && r.shockY != null && r.shockZ != null);
                    if (r.isShockExceeded && r.shockMagnitude == null) {
                        r.shockMagnitude = Math.sqrt(Math.pow(r.shockX, 2) + Math.pow(r.shockY, 2) + Math.pow(r.shockZ, 2)).toFixed(1);
                    }
                    r.isTiltExceeded = (r.tiltX || r.tiltY || r.tiltZ);
                    if (r.isTiltExceeded) {
                        r.tiltMagnitude = Math.sqrt(Math.pow(r.tiltX, 2) + Math.pow(r.tiltY, 2) + Math.pow(r.tiltZ, 2)).toFixed(1);
                    }
                }
                service.fullReports = newList;
                let filterList = service.fullReports.filter((r) => {
                    let time = moment(r.messageTimeStamp);
                    return time.isBetween(service.fromDate, service.toDate, null, '[]');
                });
                totalCount = filterList.length;
                service.reports = filterList;
                service.isFull = service.reports.length === maxReportCapacity;
            } catch (error) {
                service.feedback.addErrorFromError(error);
                console.log(error);
            } finally {
                UtilityService.loadingDecrement();
            }
        } else {
            // should only attempt this if date range is smaller and contained in the previous range
            // this doesn't even work anyway, so the calling function now sets refresh to true
            // todo: fix this feature or remove it
            const filterList = service.fullReports.filter((r) => {
                let time = moment(r.messageTimeStamp);
                return time.isBetween(service.fromDate, service.toDate, null, '[]');
            });
            totalCount = filterList.length;
            service.reports = filterList;
            service.isFull = service.reports.length === maxReportCapacity;
        }
    }

    function getReport(reportGuid) {
        let result = null;
        _.forEach(service.reports, function (report) {
            if (report.reportGuid === reportGuid) {
                result = report;
                return false;
            }
        });
        return result;
    }
}
